import PropTypes from 'prop-types'
import {
  AdasContainer,
  AdasPaper,
  AdasTypography,
  AdasBox,
  AdasLink,
  AdasRadioGroup,
  AdasSearchMenu,
  AdasTextField,
  AdasButton,
  AdasFormLabel,
  AdasImage
} from '@components/wrapper-components'
import { Autocomplete } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { InfoPopover } from '@components/popover'
import { useHistory } from 'react-router-dom'
import { GENERIC_MESSAGES, LABEL_DISPLAY, getInvoiceSummaryUrl, getQuoteSummaryUrl, getWorkOrderSummaryUrl } from '../../../constants'
import { DiagnosticTreeViewer } from '@components/diagnostic-tree-viewer'
import { getReferenceNumberBySearch, getSupportTicketsConstants } from '../../../api/api'
import { Logger } from '../../../logger'
import { useCenterStore } from '@caradasstore/CenterStore'
import { SupportRecordTypeEnums, SupportRequestEnums } from 'enums/enums'
import { useLocation } from 'react-router-dom'

export const SupportRequestPage = () => {
  const history = useHistory()
  const location = useLocation()
  const ccId = useCenterStore((store) => store.ccId)

  const { supportType: type, requestType: reqType, selectedRecord: record, referenceNumber, enableButton } = location.state || {}
  const [isOpenDiagnosticTree, setIsOpenDiagnosticTree] = useState(false)
  const [supportType, setSupportType] = useState(type || '')
  const [requestType, setRequestType] = useState(reqType || '')
  const [selectedRecord, setSelectedRecord] = useState(record || '')
  const [recordList, setRecordList] = useState([])
  const [isDisableNextButton, setIsDisableNextButton] = useState(!enableButton)
  const [supportRequestConstants, setSupportRequestConstants] = useState({})
  const [searchRecord, setSearchRecord] = useState('')
  const [recordInput, setRecordInput] = useState(record?.reference_number || '')
  const [userOverride, setUserOverride] = useState(false)

  // Automatically select Calibration Diagnostic and QuoteWorkorderInvoice radio if referenceNumber exists for the first time
  useEffect(() => {
    if (referenceNumber && !userOverride) {
      if (supportType != SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY) {
        setSupportType(SupportRequestEnums.CALIBRATION_DIAGNOSTIC)
        setRequestType(SupportRecordTypeEnums.QUOTE_WORKORDER_INVOICE)
      }
      setRecordInput(referenceNumber)
      setIsDisableNextButton(false) // Enable the Next button by default when referenceNumber exists
      if (recordList.length > 0) {
        setSelectedRecord(recordList[0]) // Auto-select the first record
      } else {
        setSelectedRecord('')
      }
    }
  }, [referenceNumber, userOverride, supportType, recordList])

  const handleNextButtonClick = () => {
    if (supportType === SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY) {
      history.push('/support/create-support-request', { selectedRecord, requestType, supportType, isViewTicketRequest: false })
    }
    setIsOpenDiagnosticTree(true)
  }

  const handleCloseModal = () => {
    setIsOpenDiagnosticTree(false)
  }

  const handleSupportType = useCallback((e) => {
    const type = e.target.value
    setIsDisableNextButton(true)
    setSelectedRecord('')
    if (!referenceNumber) {
      setRecordList([])
    }
    setSupportType(type)
    if (type === SupportRequestEnums.CALIBRATION_DIAGNOSTIC) {
      setRequestType(SupportRecordTypeEnums.QUOTE_WORKORDER_INVOICE)
      if (referenceNumber) {
        setUserOverride(false)
      }
    } else {
      setRequestType('')
      setUserOverride(true)
    }
  }, [])

  const handleTicketNumberInputChange = useCallback((e, value) => {
    if (!value) {
      setRecordList([])
    }
    setRecordInput(value?.reference_number)
    setSelectedRecord(value)
    setIsDisableNextButton(!value)
    setUserOverride(true)
  }, [])

  const handleBlur = useCallback(() => {
    if (!selectedRecord) {
      setRecordInput('')
      setRecordList([])
    }
    setUserOverride(true)
  }, [selectedRecord])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const constants = await getSupportTicketsConstants()
        setSupportRequestConstants(constants.data)
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'api', method: 'getSupportTicketsConstants' } })
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (searchRecord) {
          const listOfIds = await getReferenceNumberBySearch({
            ccId,
            search: searchRecord
          })

          return setRecordList(listOfIds.data)
        }
        setRecordList([])
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchData()
  }, [ccId, searchRecord])

  const searchRecordInputHandler = useCallback((e) => {
    setRecordInput(e.target.value)
    setUserOverride(true)
  }, [])

  useEffect(() => {
    const performRecordSearchTrigger = () => {
      setSearchRecord(recordInput)
    }

    const timeoutId = setTimeout(performRecordSearchTrigger, 300)
    return () => clearTimeout(timeoutId)
  }, [recordInput])

  const handleRecordClick = useCallback(() => {
    if (selectedRecord.table_name === 'invoice') {
      history.push(getInvoiceSummaryUrl(selectedRecord.qwi_id))
    } else if (selectedRecord.table_name === 'workorder') {
      history.push(getWorkOrderSummaryUrl(selectedRecord.qwi_id))
    } else {
      history.push(getQuoteSummaryUrl(selectedRecord.qwi_id))
    }
  }, [searchRecord, selectedRecord])

  const classes = {
    subtitle: {
      fontWeight: 700,
      lineHeight: '21.98px',
      color: 'rgba(26, 27, 31, 0.87)',
      marginTop: '10px',
      marginBottom: '10px'
    },
    assistanceStyles: {
      display: 'flex',
      alignItems: 'center'
    },
    helpSectionStyle: {
      textDecoration: 'none',
      color: '#0077cc',
      fontWeight: 600,
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      paddingLeft: '50px',
      cursor: 'pointer'
    },
    paperContainer: {
      display: 'flex',
      flexDirection: 'column',

      justifyContent: 'space-between'
    },
    formLabel: {
      display: 'flex',
      fontSize: '14px',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: '#1A1B1FDB',
      fontWeight: 700
    }
  }

  return (
    <AdasContainer maxWidth='lg'>
      <AdasPaper elevation={0} className='p-5 mb-[20px] mt-5'>
        <AdasBox className='min-h-[74vh]'>
          <AdasTypography className='font-bold leading-[21.98px] text-[rgba(26,27,31,0.87)] my-[10px]'>
            {GENERIC_MESSAGES.CREATE_SUPPORT_REQUEST}
          </AdasTypography>
          <AdasBox className='p-[1%] min-h-[70px] bg-[#FFFACA]'>
            <AdasBox className='flex items-center'>
              <InfoPopover iconColor={'#001B3F'} ariaLabel='Information on default' enableClick={false} />
              <AdasTypography>{GENERIC_MESSAGES.SUPPORT_REQUESTING_ASSITANCE}</AdasTypography>
            </AdasBox>
            <AdasLink
              className='no-underline text-[#0077cc] font-semibold block truncate pl-[50px] cursor-pointer'
              rel='noopener noreferrer'
              onClick={() => history.push('/help')}
            >
              {LABEL_DISPLAY.HELP_SECTION_LABEL}
            </AdasLink>
          </AdasBox>
          <AdasBox variant='outlined' className='pt-[10px] mt-[20px] min-h-[100px]'>
            <AdasBox className='text-right my-[.6rem] mx-4'>
              <AdasFormLabel
                id='repair-order-label'
                align='right'
                className='flex text-sm justify-between items-center text-[#1A1B1FDB] font-bold'
              >
                {LABEL_DISPLAY.SUPPORT_TYPE_LABEL}
              </AdasFormLabel>
              {supportRequestConstants?.supportTypes && (
                <AdasRadioGroup
                  options={(supportRequestConstants?.supportTypes || []).map((item) => ({
                    value: item.slug,
                    label: item.type
                  }))}
                  row
                  name='supportType'
                  value={supportType}
                  onChange={handleSupportType}
                />
              )}
            </AdasBox>
          </AdasBox>
          {supportType !== undefined && (
            <AdasBox variant='outlined' className='pt-[10px] mt-[20px] min-h-[100px]'>
              <AdasBox className='text-left my-[.6rem] mx-4'>
                <AdasFormLabel
                  id='repair-order-label'
                  align='right'
                  className='flex text-sm justify-between items-center text-[#1A1B1FDB] font-bold'
                >
                  {LABEL_DISPLAY.REQUEST_TYPE_LABEL}
                </AdasFormLabel>
                {supportType === SupportRequestEnums.CALIBRATION_DIAGNOSTIC && (
                  <AdasRadioGroup
                    options={(supportRequestConstants?.supportRecordTypes || [])
                      .filter((item) => item.slug !== SupportRecordTypeEnums.INFORMATION_REQUEST) //CAR-1627 hidden Information Request radio button
                      .map((item) => ({
                        value: item.slug,
                        label: item.recordType
                      }))}
                    row
                    name='requestType'
                    value={requestType}
                    onChange={(e) => {
                      setIsDisableNextButton(e.target.value === SupportRequestEnums.QUOTE_WORKORDER_INVOICE)
                      setRequestType(e.target.value)
                      if (referenceNumber && e.target.value === SupportRequestEnums.QUOTE_WORKORDER_INVOICE) {
                        setSelectedRecord(referenceNumber)
                        setUserOverride(false)
                      } else {
                        setSelectedRecord('')
                        setUserOverride(true)
                      }
                    }}
                  />
                )}
                {supportType === SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY && (
                  <AdasSearchMenu
                    fullWidth
                    id='it-RequestType'
                    select
                    label='Type'
                    className='mt-[20px] w-[40%]'
                    variant='outlined'
                    onChange={(e) => {
                      setIsDisableNextButton(e.target.value === SupportRequestEnums.QUOTE_WORKORDER_INVOICE)
                      setRequestType(e.target.value)
                      if (referenceNumber && e.target.value === SupportRequestEnums.QUOTE_WORKORDER_INVOICE) {
                        setSelectedRecord(referenceNumber)
                        setUserOverride(false)
                      } else {
                        setSelectedRecord('')
                        setUserOverride(true)
                      }
                    }}
                    name='requestType'
                    value={requestType}
                    options={(supportRequestConstants?.supportItRequestTypes || []).map((item) => ({
                      value: item.slug,
                      name: item.itRequestType
                    }))}
                  />
                )}
              </AdasBox>
              {requestType === SupportRequestEnums.QUOTE_WORKORDER_INVOICE && (
                <AdasBox className='my-[.6rem] mx-4 mt-[20px]'>
                  <Autocomplete
                    id='record-search'
                    options={recordList}
                    getOptionLabel={(option) => option.reference_number}
                    value={selectedRecord || null}
                    isOptionEqualToValue={(option, value) => option.reference_number === value.reference_number}
                    onBlur={handleBlur}
                    onInput={searchRecordInputHandler}
                    onChange={handleTicketNumberInputChange}
                    className='w-[40%]'
                    renderInput={(params) => <AdasTextField {...params} label='Record #' variant='outlined' />}
                  />
                </AdasBox>
              )}
              {selectedRecord && (
                <AdasBox className='justify-between p-4 mb-[20px]'>
                  <AdasBox className='flex'>
                    <AdasBox className='mt-[20px] w-[30%]'>
                      <AdasTypography variant='subtitle2' className='mb-[20px]'>
                        <AdasLink underline='none' className='cursor-pointer' onClick={handleRecordClick}>
                          RECORD #{selectedRecord.reference_number}
                        </AdasLink>
                      </AdasTypography>
                      <AdasTypography className='text-sm'>
                        {selectedRecord.year} {selectedRecord.make} {selectedRecord.model}
                      </AdasTypography>
                      <AdasTypography className='text-sm mt-[5px]'>VIN: {selectedRecord.vin}</AdasTypography>
                    </AdasBox>
                    <AdasBox className='w-[70%]'>
                      <AdasImage src={selectedRecord.image_url} alt='vehicle' className='max-w-[200px] max-h-[250px]' />
                    </AdasBox>
                  </AdasBox>
                </AdasBox>
              )}
            </AdasBox>
          )}
        </AdasBox>
        <AdasBox className='flex justify-between mt-[10px]'>
          <AdasButton className='mr-4 ml-auto' onClick={() => history.replace('/support')}>
            CANCEL
          </AdasButton>
          <AdasButton variant='contained' disabled={isDisableNextButton} onClick={handleNextButtonClick}>
            NEXT
          </AdasButton>
        </AdasBox>
      </AdasPaper>
      <DiagnosticTreeViewer
        handleNextButtonClick={() => {
          handleCloseModal()
          history.push('/support/create-support-request', { selectedRecord, requestType, supportType, isViewTicketRequest: false })
        }}
        closeModal={handleCloseModal}
        isOpen={isOpenDiagnosticTree}
      />
    </AdasContainer>
  )
}

SupportRequestPage.propTypes = {
  title: PropTypes.string
}
